import React, { useRef, useState, useLayoutEffect } from 'react';
import { Box, Flex, ScrollArea, Stack } from '@mantine/core';

type FooterAlign = 'left' | 'center' | 'right';

type FlowBodyProps = {
  children: React.ReactNode;
  footer?: React.ReactNode;
  footerAlign?: FooterAlign;
};

const FOOTER_BOTTOM_OFFSET = 16;
const FOOTER_EXTRA_SPACING = 32;

const justifyByAlign: Record<FooterAlign, string> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

export function FlowBody({ children, footer, footerAlign = 'center' }: FlowBodyProps) {
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = useState(0);

  useLayoutEffect(() => {
    if (!footerRef.current) return;

    const updateHeight = () => {
      if (footerRef.current) {
        setFooterHeight(footerRef.current.getBoundingClientRect().height);
      }
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerRef.current);
    return () => observer.disconnect();
  }, [!!footer]);

  const justify = justifyByAlign[footerAlign];

  const spacerHeight = footer ? footerHeight + FOOTER_BOTTOM_OFFSET + FOOTER_EXTRA_SPACING : 0;

  return (
    <Stack gap={0} h={'100%'} pos={'relative'}>
      <ScrollArea
        flex={1}
        mih={0}
        px="md"
        scrollbarSize={8}
        type="auto"
        styles={{
          scrollbar: {
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            borderRadius: 24,
            marginBlock: 8,
            padding: 0,
          },
          thumb: {
            backgroundColor: 'black',
            borderRadius: 24,
            '&:hover': {
              backgroundColor: '#333',
            },
          },
          corner: {
            display: 'none',
          },
        }}
      >
        <Flex direction="column" mih="100%">
          <Flex flex={1} mih={0} direction="column">
            {children}
          </Flex>
          {footer && <Box style={{ minHeight: spacerHeight, flexShrink: 0 }} />}
        </Flex>
      </ScrollArea>

      {footer && (
        <Box
          ref={footerRef}
          px="sm"
          py="sm"
          pos="absolute"
          bottom={FOOTER_BOTTOM_OFFSET}
          left="50%"
          w="fit-content"
          maw="calc(100% - 32px)"
          style={theme => ({
            transform: 'translateX(-50%)',
            zIndex: 10,

            backgroundColor: 'rgba(255, 255, 255, 0.5)',
            backdropFilter: 'blur(10px)',

            borderRadius: theme.radius.xl,
            boxShadow: '0 10px 30px theme.black',
          })}
        >
          <Flex justify={justify} gap="sm" wrap="nowrap">
            {footer}
          </Flex>
        </Box>
      )}
    </Stack>
  );
}
